import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { fmtDate } from "../lib/format";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOC = { A_STORE: "Store", A_ROOM: "Room", A_REPAIR: "Under Repair", A_DAMAGED: "Damaged Holding", A_LOST: "Lost/Disposed" };

export default function AssetUnitDetail() {
  const { assetId } = useParams();
  const [u, setU] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/assets/units/${assetId}`).then(({ data }) => setU(data)).catch(() => setErr(true));
  }, [assetId]);

  if (err) return <Layout><Card className="p-6 text-center text-sm text-red-600">Asset not found or you don't have access.</Card></Layout>;
  if (!u) return <Layout><div className="text-sm text-slate-500 p-8 text-center">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="max-w-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="section-title" data-testid="unit-title">{u.asset_id}</h1>
            <div className="section-sub">{u.item_name}</div>
          </div>
          <img src={`${API_BASE}/assets/units/${u.asset_id}/qr`} alt="QR" className="w-24 h-24 border border-slate-200 rounded" />
        </div>

        <Card className="p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div><div className="text-xs text-slate-400">Location</div><Badge className="bg-slate-900 text-white mt-0.5">{u.location === "A_ROOM" ? `Room ${u.room_number || ""}` : LOC[u.location] || u.location}</Badge></div>
            <div><div className="text-xs text-slate-400">Status</div><div className="font-medium capitalize">{u.status?.replace("_", " ")}</div></div>
            <div><div className="text-xs text-slate-400">Condition</div><div className="font-medium">{u.condition || "—"}</div></div>
            <div><div className="text-xs text-slate-400">Brand / Model</div><div>{[u.brand, u.model].filter(Boolean).join(" / ") || "—"}</div></div>
            <div><div className="text-xs text-slate-400">Serial No.</div><div className="font-mono text-xs">{u.serial_number || "—"}</div></div>
            <div><div className="text-xs text-slate-400">Rate</div><div>{u.rate != null ? `₹${u.rate}` : "—"}</div></div>
            <div><div className="text-xs text-slate-400">Warranty</div><div className="text-xs">{u.warranty?.end ? `until ${u.warranty.end}` : "—"}</div></div>
            <div><div className="text-xs text-slate-400">AMC</div><div className="text-xs">{u.amc?.end ? `${u.amc.provider || ""} until ${u.amc.end}` : "—"}</div></div>
          </div>
        </Card>

        {u.maintenance?.length > 0 && (
          <Card className="p-4 mb-4">
            <div className="section-sub mb-2 font-medium text-slate-700">Maintenance History</div>
            {u.maintenance.map(j => (
              <div key={j.id} className="flex justify-between border-b border-slate-100 py-1.5 text-sm">
                <span className="font-mono text-xs">{j.number}</span><span className="text-xs truncate mx-2 flex-1">{j.description}</span>
                <Badge variant="outline" className="text-xs">{j.status}</Badge>
              </div>
            ))}
          </Card>
        )}

        <Card className="p-4">
          <div className="section-sub mb-2 font-medium text-slate-700">Full History</div>
          {(u.history || []).slice().reverse().map((h, i) => (
            <div key={`${h.at || "h"}-${i}`} className="border-b border-slate-100 py-1.5 text-sm">
              <span className="text-xs text-slate-400 mr-2">{fmtDate(h.at)}</span>
              <span>{h.event}</span>
              {h.reason && <span className="text-xs text-slate-500 ml-2">({h.reason})</span>}
              <span className="text-xs text-slate-400 ml-2">— {h.by}</span>
            </div>
          ))}
        </Card>
      </div>
    </Layout>
  );
}
